import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/AdminLayout';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Package, ShoppingBag, Users, Tag, DollarSign, TrendingUp, Truck, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  pending: 'text-amber-600', paid: 'text-blue-600', processing: 'text-cyan-700',
  shipped: 'text-purple-600', delivered: 'text-green-600', cancelled: 'text-red-600', refunded: 'text-orange-600'
};

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="admin-card rounded-xl p-5 flex items-center gap-4" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon className="w-6 h-6" />
      </div>
      <div>
        <p className="text-ink/55 text-xs uppercase tracking-widest">{label}</p>
        <p className="text-ink text-2xl font-black">{value}</p>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/admin/stats`, { withCredentials: true })
      .then(r => setStats(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <AdminLayout><div className="flex justify-center pt-20"><div className="w-8 h-8 border-2 border-honey border-t-transparent rounded-full animate-spin" /></div></AdminLayout>;

  return (
    <AdminLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-black text-ink" style={{ fontFamily: 'Outfit, sans-serif' }}>Dashboard</h1>
        <p className="text-ink/55 text-sm">Store overview</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Revenue" value={`$${stats?.total_revenue?.toFixed(0) || 0}`} color="bg-honey/10 text-honey-700" />
        <StatCard icon={ShoppingBag} label="Orders" value={stats?.total_orders || 0} color="bg-honey/10 text-honey-600" />
        <StatCard icon={Package} label="Products" value={stats?.total_products || 0} color="bg-purple-500/10 text-purple-600" />
        <StatCard icon={Users} label="Customers" value={stats?.total_customers || 0} color="bg-green-500/10 text-green-600" />
        <StatCard icon={Tag} label="Active Coupons" value={stats?.total_coupons || 0} color="bg-pink-500/10 text-pink-600" />
        <StatCard icon={TrendingUp} label="Avg Order" value={stats?.total_orders ? `$${(stats.total_revenue / stats.total_orders).toFixed(0)}` : '$0'} color="bg-orange-500/10 text-orange-600" />
      </div>

      {(stats?.orders_to_ship > 0 || stats?.out_of_stock > 0 || stats?.low_stock_count > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
          <Link to="/admin/orders?status=pending,paid,processing" className="admin-card rounded-xl p-5 flex items-center gap-4 hover:border-honey/30 transition-colors">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-honey/10 text-honey-600"><Truck className="w-6 h-6" /></div>
            <div>
              <p className="text-ink text-2xl font-black">{stats.orders_to_ship}</p>
              <p className="text-ink/55 text-xs uppercase tracking-widest">Orders waiting to ship</p>
            </div>
          </Link>
          <div className="admin-card rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-red-600" />
              <p className="text-ink font-bold text-sm">Stock alerts</p>
              <Link to="/admin/products?stock=out" className="ml-auto text-xs text-red-600 hover:underline">{stats.out_of_stock} out</Link>
              <Link to="/admin/products?stock=low" className="text-xs text-amber-600 hover:underline">{stats.low_stock_count} low</Link>
            </div>
            <div className="space-y-1">
              {stats.low_stock.map(p => (
                <div key={p.id} className="flex justify-between text-xs">
                  <span className="text-ink/70 truncate">{p.title}{p.sku ? <span className="text-ink/45 font-mono ml-2">{p.sku}</span> : null}</span>
                  <span className={p.inventory <= 0 ? 'text-red-600 font-bold' : 'text-amber-600 font-bold'}>{p.inventory}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {stats?.monthly_stats?.length > 0 && (
        <div className="admin-card rounded-xl p-6 mb-8">
          <h2 className="text-ink font-bold mb-4">Revenue by Month</h2>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.monthly_stats}>
              <XAxis dataKey="month" stroke="#1F1A1420" tick={{ fill: '#1F1A1499', fontSize: 11 }} />
              <YAxis stroke="#1F1A1420" tick={{ fill: '#1F1A1499', fontSize: 11 }} />
              <Tooltip contentStyle={{ background: '#FFFFFF', border: '1px solid #EEE7DA', borderRadius: '8px', color: '#1F1A14' }} />
              <Bar dataKey="revenue" fill="#F5B301" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      {stats?.recent_orders?.length > 0 && (
        <div className="admin-card rounded-xl p-6">
          <h2 className="text-ink font-bold mb-4">Recent Orders</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-ink/55 text-xs uppercase tracking-widest border-b border-ink/10">
                  <th className="text-left pb-3">Order</th>
                  <th className="text-left pb-3">Customer</th>
                  <th className="text-left pb-3">Total</th>
                  <th className="text-left pb-3">Status</th>
                  <th className="text-left pb-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink/5">
                {stats.recent_orders.map(order => (
                  <tr key={order.id} className="text-ink/75">
                    <td className="py-3 font-mono text-xs"><Link to={`/admin/orders/${order.id}`} className="text-honey-700 hover:underline">{order.order_number}</Link></td>
                    <td className="py-3 truncate max-w-[150px]">{order.customer_email}</td>
                    <td className="py-3 font-bold text-ink">${order.total?.toFixed(2)}</td>
                    <td className="py-3"><span className={`capitalize ${STATUS_COLORS[order.status]}`}>{order.status}</span></td>
                    <td className="py-3 text-ink/55">{new Date(order.created_at).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
